import Guard from 'arg-guard'

import Service from './service'
import ServiceOperation from './service-operation'
import Logger from '@/logger'

/**
 * Gateway for accessing the auth service.
 *
 * @export AuthService
 * @class AuthService
 */
export default class AuthService extends Service {

    /**
     * Creates an instance of AuthService.
     * @param {?number} schemeId The scheme for the active context.
     * @memberof AuthService
     */
    constructor({ schemeId } = { schemeId: null }) {
        super('/auth', 'Auth')

        Guard.throwIf(schemeId, 'schemeId')
        this.schemeId = schemeId
    }

    /**
     * Changes the password for the currently logged in user.
     * @param {string} oldPassword
     * @param {string} newPassword
     * @param {string} confirmPassword
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof AuthService
     */
    async changePasswordAsync(oldPassword, newPassword, confirmPassword) {
        const operationState = new ServiceOperation('Change Password', true)
        const url = `change-pw`
        const data = {
            oldPassword,
            newPassword,
            confirmPassword,
            schemeId: this.schemeId,
        }

        Logger.logObject(data, this.baseUrl + '/' + url, 'Request [POST]')
        return await this._processPost(url, data, operationState, 'changePasswordAsync')
    }

    /**
     * Creates a guest owner in the back end and returns a unique dynamic key.
     * @param {number} addressId An optional address id to create a linked household for the guest owner.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof AuthService
     */
    async createGuestAsync(addressId) {
        const operationState = new ServiceOperation('Create guest owner', true)
        const url = addressId === undefined || addressId === null || addressId < 1
            ? `welcome-guest`
            : `welcome-guest/for-address/${ addressId }`

        Logger.log(this.baseUrl + '/' + url, 'Request [PUT]')
        return await this._processPut(url, {}, operationState, 'createGuestAsync')
    }

    /**
     * Generates a forgotten password request on the server.
     * @param {string} email
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof AuthService
     */
    async forgotPasswordAsync(email) {
        const operationState = new ServiceOperation('Forgotten Password request', true)
        const url = `forgot-pw`

        const data = {
            email,
            schemeId: this.schemeId,
        }

        Logger.logObject(data, `${ this.baseUrl }/${ url }`, 'Request [POST]')
        return await this._processPost(url, data, operationState, 'forgotPasswordAsync')
    }

    /**
     * Load improvements from an address ID.
     * @param {string} id The guest id / dynamic key.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ImprovementService
     */
    async loadGuestAsync(id) {
        Guard.throwIf(id, 'id')

        const operationState = new ServiceOperation('Guest Info Load', true)
        const url = `guest/${ id }`

        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')
        return await this._processGet(url, operationState, 'loadGuestAsync')
    }

    /**
     * Logs in an external user.
     * @param {string} key
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof AuthService
     */
    async logInExternalAsync(key) {
        const operationState = new ServiceOperation('Log In External', true)
        const url = 'login-external'
        const data = {
            key,
        }

        Logger.logObject(data, `${ this.baseUrl }/${ url }`, 'Request [POST]')
        Logger.logObject(this._buildAxiosConfig(), `Request headers`, 'Login')
        return await this._processPost(url, data, operationState, 'logInExternalAsync')
    }

    /**
     * Logs in a user.
     * @param {string} email
     * @param {string} password
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof AuthService
     */
    async logInAsync(email, password) {
        const operationState = new ServiceOperation('Log In', true)
        const url = `login`
        const data = {
            email,
            password,
            schemeId: this.schemeId,
        }

        Logger.logObject(data, `${ this.baseUrl }/${ url }`, 'Request [POST]')
        Logger.logObject(this._buildAxiosConfig(), `Request headers`, 'Login')
        return await this._processPost(url, data, operationState, 'logInAsync')
    }

    /**
     * Registers a new user account.
     * @param {AuthServiceRegistrationData} data The registration form data.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof AuthService
     */
    async registerAsync(data) {
        const operationState = new ServiceOperation('Registration', true)
        const url = 'register'

        Logger.logObject(data, `${ this.baseUrl }/${ url }`, 'Request [POST]')
        return await this._processPost(url, { ...data, schemeId: this.schemeId }, operationState, 'registerAsync')
    }

    /**
     * Resets a password using an earlier, server generated, token.
     * The token needs to have spaces and bad encoding replaced with + characters as these are otherwise lost in translation.
     * @param {number} userId
     * @param {string} code A code generated by an earlier call to forgottenPasswordAsync.
     * @param {string} newPassword
     * @param {string} confirmPassword
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof AuthService
     */
    async resetPasswordAsync(userId, code, newPassword, confirmPassword) {
        const operationState = new ServiceOperation('Reset Password', true)
        const url = `reset-pw`
        const data = {
            userId,
            code: code.replace(/ /g, '+').replace(/%20/g, '+'),
            newPassword,
            confirmPassword,
            schemeId: this.schemeId,
        }

        Logger.logObject(data, `${ this.baseUrl }/${ url }`, 'Request [POST]')
        return await this._processPost(url, data, operationState, 'resetPasswordAsync')
    }
}
